import { AccountTransactions } from '@account'
import { BankOutlineIcon, CustomPreloader, MMButton, PageHeader, PageTitle, SectionCard } from '@components'
import QuickActionsBar from '@dashboard/components/QuickActionsBar'
import StaffAccountCards from '@dashboard/components/StaffAccountCards'
import StaffTransactionsEmpty from '@dashboard/components/StaffTransactionsEmpty'
import cn from '@integrations/classnames'
import { useMemo } from '@integrations/react'
import { useNavigate } from '@integrations/routing'
import { SpendingLimitBarUser, SpendingLimitPlaceholder } from '@requests'
import { useAppDispatch } from '@store'
import { isOpenAccount } from '@utils'
import {
  api,
  paths,
  useGetMyAccountAccessQuery,
  useMerchantAccounts,
  useMyAccountAccessQry
} from 'mmfintech-backend-api'
import { tr } from 'mmfintech-commons'
import styles from './DashboardStaff.module.scss'

const LATEST_TRANSACTIONS_COUNT = 25

// Staff home: the accounts they can access, their latest activity and their own spending limit.
// Everything shown comes from data scoped to the signed-in staff member.
function DashboardStaff() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const { hasAnyWritePermission, hasReadPermission, getAccountAccessLevel, accountAccessFetching } =
    useMyAccountAccessQry()
  // Same cached request as the hook above: its first load gates the page, its refetch backs "Check again"
  const { isLoading: accountAccessLoading, refetch: refetchAccountAccess } = useGetMyAccountAccessQuery()
  const { paymentAccounts, accountsLoading, accountsFetching } = useMerchantAccounts()

  const accounts = useMemo(
    () => paymentAccounts.filter((account) => isOpenAccount(account) && hasReadPermission(account.id)),
    [paymentAccounts, hasReadPermission]
  )
  const hasAccounts = accounts.length > 0

  function handleCheckAgain() {
    refetchAccountAccess()
    dispatch(api.util.invalidateTags(['Accounts']))
  }

  // Only the first load: a later refetch keeps the page and shows on the button that started it
  if (accountAccessLoading || accountsLoading) {
    return <CustomPreloader />
  }

  const noTransactions = <StaffTransactionsEmpty hasAccounts={hasAccounts} canPay={hasAnyWritePermission} />

  return (
    <>
      <PageHeader>
        <PageTitle>{tr('FRONTEND.DASHBOARD.HEADING', 'Overview')}</PageTitle>
        {hasAccounts && <QuickActionsBar />}
      </PageHeader>

      <div className={cn(styles['staff-overview'], 'staff-overview')}>
        {hasAccounts ? (
          <StaffAccountCards accounts={accounts} getAccessLevel={getAccountAccessLevel} />
        ) : (
          <SectionCard
            icon={<BankOutlineIcon size={18} />}
            title={tr('FRONTEND.DASHBOARD.STAFF.NO_ACCOUNTS', "You don't have access to any accounts yet")}
            description={tr(
              'FRONTEND.DASHBOARD.STAFF.NO_ACCOUNTS_HINT',
              'An administrator decides which accounts you can see and use. Once they give you access, your balances and payments will show here.'
            )}
            aside={
              <MMButton
                variant='dashboard'
                caption={tr('FRONTEND.DASHBOARD.STAFF.CHECK_AGAIN', 'Check again')}
                loading={accountAccessFetching || accountsFetching}
                onClick={handleCheckAgain}
                data-test='staff-check-again'
              />
            }
            data-test='staff-dashboard-no-accounts'
          />
        )}

        <SectionCard
          className={cn('staff-latest-transactions')}
          title={tr('FRONTEND.DASHBOARD.STAFF.LATEST_TRANSACTIONS', 'Latest transactions')}
          aside={
            hasAccounts && (
              <MMButton
                variant='primary'
                caption={tr('FRONTEND.DASHBOARD.STAFF.SEE_ALL_TRANSACTIONS', 'See all transactions')}
                onClick={() => navigate(paths.banking.transactions.list())}
                data-test='staff-see-all-transactions'
              />
            )
          }
          data-test='staff-latest-transactions'
        >
          {hasAccounts ? (
            <AccountTransactions size={LATEST_TRANSACTIONS_COUNT} empty={noTransactions} fitHeight compact />
          ) : (
            noTransactions
          )}
        </SectionCard>

        {!hasAccounts ? <SpendingLimitPlaceholder /> : hasAnyWritePermission && <SpendingLimitBarUser />}
      </div>
    </>
  )
}

export default DashboardStaff
